import { type Locale, PAGE_NAMES } from '~/shared/constants';
import type { TypedTranslate } from '~/shared/types/i18n-type';

const DEFAULT_OG_IMAGE =
  'https://prana-club-bucket.s3.eu-central-1.amazonaws.com/ua/shared/images/prana-club-banner.png';

const BASE_URL = process.env.NODE_ENV === 'production' ? 'https://prana-club.com' : 'http://localhost:3000';

export type TranslateFunction = (key: string, options?: { ns?: string[] }) => string;

type OpenGraphType = 'website' | 'article' | 'profile';

type OpenGraphImage = {
  url: string;
};

type OpenGraphPropsType = {
  title: string;
  description: string;
  siteName: string;
  url: string;
  images: OpenGraphImage[];
  type: OpenGraphType;
};

type PageConfigType = {
  path: string;
  title: string;
  metaTitle: string;
  metaDescription: string;
  openGraph: OpenGraphPropsType;
  alias?: string;
  filter?: string;
};

type GetPageConfigPropsType = {
  t: TypedTranslate;
  path: string;
  lng: Locale;
  title?: string;
  image?: string;
  filter?: string;
  alias?: string | string[];
};

type StaticPageDefinition = {
  titleKey: string;
  descriptionKey: string;
  url?: string;
  ogType?: OpenGraphType;
};

const STATIC_PAGE_DEFINITIONS: Partial<Record<string, StaticPageDefinition>> = {
  [PAGE_NAMES.UNSUBSCRIBE_USER]: {
    titleKey: 'page.unsubscribe-user.title',
    descriptionKey: 'page.unsubscribe-user.meta-description',
  },
  [PAGE_NAMES.LANDINGS_SPEED_READING_FOR_ADULTS]: {
    titleKey: 'page.speed-reading-for-adults.title',
    descriptionKey: 'page.speed-reading-for-adults.meta-description',
  },
  [PAGE_NAMES.THANK_YOU_FOR_SENDING_REQUEST]: {
    titleKey: 'page.thank-you.title',
    descriptionKey: 'page.thank-you.meta-description',
    url: PAGE_NAMES.THANK_YOU_FOR_SENDING_REQUEST,
  },
  [PAGE_NAMES.SUPPORT]: {
    titleKey: 'page.support.title',
    descriptionKey: 'page.support.meta-description',
  },
  [PAGE_NAMES.START_COURSE]: {
    titleKey: 'page.start-course.title',
    descriptionKey: 'page.start-course.meta-description',
  },
  [PAGE_NAMES.SIGN_UP]: {
    titleKey: 'page.sign-up.title',
    descriptionKey: 'page.sign-up.meta-description',
  },
  [PAGE_NAMES.SIGN_IN]: {
    titleKey: 'page.sign-in.title',
    descriptionKey: 'page.sign-in.meta-description',
  },
  [PAGE_NAMES.CLIENTS_PRODUCTS]: {
    titleKey: 'page.clients.products.title',
    descriptionKey: 'page.clients.products.meta-description',
  },
  [PAGE_NAMES.CLIENTS_DASHBOARD]: {
    titleKey: 'page.clients.dashboard.title',
    descriptionKey: 'page.clients.dashboard.meta-description',
  },
  [PAGE_NAMES.CLIENTS_CONNECT_PLATFORM]: {
    titleKey: 'page.clients.connect-platform.title',
    descriptionKey: 'page.clients.connect-platform.meta-description',
  },
  [PAGE_NAMES.CLIENTS_WORKSPACE]: {
    titleKey: 'page.clients.workspace.title',
    descriptionKey: 'page.clients.workspace.meta-description',
  },
  [PAGE_NAMES.CLIENTS_INFLUENCERS]: {
    titleKey: 'page.clients.influencers.title',
    descriptionKey: 'page.clients.influencers.meta-description',
  },
  [PAGE_NAMES.CLIENTS_PAYMENTS]: {
    titleKey: 'page.clients.payments.title',
    descriptionKey: 'page.clients.payments.meta-description',
  },
  [PAGE_NAMES.CLIENTS_REQUESTS]: {
    titleKey: 'page.clients.requests.title',
    descriptionKey: 'page.clients.requests.meta-description',
  },
  [PAGE_NAMES.CLIENTS_STATISTICS]: {
    titleKey: 'page.clients.statistics.title',
    descriptionKey: 'page.clients.statistics.meta-description',
  },
  [PAGE_NAMES.CLIENTS_NOTIFICATIONS]: {
    titleKey: 'page.clients.notifications.title',
    descriptionKey: 'page.clients.notifications.meta-description',
  },
  [PAGE_NAMES.CLIENTS_SETTINGS]: {
    titleKey: 'page.clients.settings.title',
    descriptionKey: 'page.clients.settings.meta-description',
  },
  [PAGE_NAMES.CLIENTS_MANAGERS]: {
    titleKey: 'page.clients.managers.title',
    descriptionKey: 'page.clients.managers.meta-description',
  },
  [PAGE_NAMES.CLIENTS_COMPANIES]: {
    titleKey: 'page.clients.companies.title',
    descriptionKey: 'page.clients.companies.meta-description',
  },
  [PAGE_NAMES.CLIENTS_SIGN_IN]: {
    titleKey: 'page.clients.sign-in.title',
    descriptionKey: 'page.clients.sign-in.meta-description',
  },
  [PAGE_NAMES.CLIENTS_SIGN_UP]: {
    titleKey: 'page.clients.sign-up.title',
    descriptionKey: 'page.clients.sign-up.meta-description',
  },
  [PAGE_NAMES.CLIENTS_FOUNDERS]: {
    titleKey: 'page.clients.founders.title',
    descriptionKey: 'page.clients.founders.meta-description',
  },
  [PAGE_NAMES.CLIENTS_PROFILE]: {
    titleKey: 'page.clients.profile.title',
    descriptionKey: 'page.clients.profile.meta-description',
  },
  [PAGE_NAMES.RESET_PASSWORD]: {
    titleKey: 'page.reset-password.title',
    descriptionKey: 'page.reset-password.meta-description',
  },
  [PAGE_NAMES.HOME]: {
    titleKey: 'page.home.title',
    descriptionKey: 'page.home.meta-description',
  },
  [PAGE_NAMES.BLOG]: {
    titleKey: 'page.blog.title',
    descriptionKey: 'page.blog.meta-description',
  },
  [PAGE_NAMES.COURSES]: {
    titleKey: 'page.courses.title',
    descriptionKey: 'page.courses.meta-description',
  },
  [PAGE_NAMES.CONTACTS]: {
    titleKey: 'page.contacts.title',
    descriptionKey: 'page.contacts.meta-description',
  },
  [PAGE_NAMES.CATEGORIES]: {
    titleKey: 'page.categories.title',
    descriptionKey: 'page.categories.meta-description',
  },
  [PAGE_NAMES.ACCOUNT_CONFIRMATION]: {
    titleKey: 'page.account-confirmation.title',
    descriptionKey: 'page.account-confirmation.meta-description',
  },
  [PAGE_NAMES.ABOUT_US]: {
    titleKey: 'page.about-us.title',
    descriptionKey: 'page.about-us.meta-description',
  },
  [PAGE_NAMES.SERVER_ERROR]: {
    titleKey: 'page.server-error.title',
    descriptionKey: 'page.server-error.meta-description',
  },
  [PAGE_NAMES.NOT_FOUND]: {
    titleKey: 'page.not-found.title',
    descriptionKey: 'page.not-found.meta-description',
  },
  [PAGE_NAMES.USER_PROFILE]: {
    titleKey: 'page.user-profile.title',
    descriptionKey: 'page.user-profile.meta-description',
    ogType: 'profile',
  },
  [PAGE_NAMES.POSTS_SEARCH]: {
    titleKey: 'page.posts-search.title',
    descriptionKey: 'page.posts-search.meta-description',
  },
  [PAGE_NAMES.PLATFORM_TEACHERS]: {
    titleKey: 'page.platform.teachers.title',
    descriptionKey: 'page.platform.teachers.meta-description',
  },
  [PAGE_NAMES.PLATFORM_CALENDAR]: {
    titleKey: 'page.platform.calendar.title',
    descriptionKey: 'page.platform.calendar.meta-description',
  },
  [PAGE_NAMES.PLATFORM_COURSES]: {
    titleKey: 'page.platform.online-courses.title',
    descriptionKey: 'page.platform.online-courses.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE]: {
    titleKey: 'page.platform.online-course.title',
    descriptionKey: 'page.platform.online-course.meta-description',
  },
  [PAGE_NAMES.PLATFORM_VIDEO_COURSES]: {
    titleKey: 'page.platform.video-courses.title',
    descriptionKey: 'page.platform.video-courses.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_CATEGORIES]: {
    titleKey: 'page.platform.online-course-categories.title',
    descriptionKey: 'page.platform.online-course-categories.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_SURVEYS]: {
    titleKey: 'page.platform.online-course-surveys.title',
    descriptionKey: 'page.platform.online-course-surveys.meta-description',
  },
  [PAGE_NAMES.PLATFORM_VIDEO_COURSE_CATEGORIES]: {
    titleKey: 'page.platform.video-course-categories.title',
    descriptionKey: 'page.platform.video-course-categories.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_TRIAL_LESSON]: {
    titleKey: 'page.platform.online-course-trial-lesson.title',
    descriptionKey: 'page.platform.online-course-trial-lesson.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_THANK_YOU]: {
    titleKey: 'page.platform.online-course-thank-you.title',
    descriptionKey: 'page.platform.online-course-thank-you.meta-description',
  },
  [PAGE_NAMES.PLATFORM_VIDEO_COURSE_THANK_YOU]: {
    titleKey: 'page.platform.video-course-thank-you.title',
    descriptionKey: 'page.platform.video-course-thank-you.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_LESSON_CALL]: {
    titleKey: 'page.platform.online-course-lesson.title',
    descriptionKey: 'page.platform.online-course-lesson.meta-description',
  },
  [PAGE_NAMES.PLATFORM_ONLINE_COURSE_LESSON_RESULTS]: {
    titleKey: 'page.platform.online-course-lesson-results.title',
    descriptionKey: 'page.platform.online-course-lesson-results.meta-description',
  },
  [PAGE_NAMES.PLATFORM_USER_PROFILE]: {
    titleKey: 'page.platform.user-profile.title',
    descriptionKey: 'page.platform.user-profile.meta-description',
  },
  [PAGE_NAMES.PLATFORM_USER_FAVORITE_TOPICS]: {
    titleKey: 'page.platform.user-favorite-topics.title',
    descriptionKey: 'page.platform.user-favorite-topics.meta-description',
  },
  [PAGE_NAMES.PLATFORM_USER_DIALOGS]: {
    titleKey: 'page.platform.dialogs.title',
    descriptionKey: 'page.platform.dialogs.meta-description',
  },
  [PAGE_NAMES.PLATFORM_MANAGERS]: {
    titleKey: 'page.platform.managers.title',
    descriptionKey: 'page.platform.managers.meta-description',
  },
  [PAGE_NAMES.PLATFORM_STUDENTS_LIST]: {
    titleKey: 'page.platform.students-list.title',
    descriptionKey: 'page.platform.students-list.meta-description',
  },
  [PAGE_NAMES.PLATFORM_STUDENTS_BOARD]: {
    titleKey: 'page.platform.students-board.title',
    descriptionKey: 'page.platform.students-board.meta-description',
  },
  [PAGE_NAMES.ADMIN_CATEGORIES]: {
    titleKey: 'page.admin.categories.title',
    descriptionKey: 'page.admin.categories.meta-description',
  },
  [PAGE_NAMES.ADMIN_COMPANIES]: {
    titleKey: 'page.admin.companies.title',
    descriptionKey: 'page.admin.companies.meta-description',
  },
  [PAGE_NAMES.ADMIN_USERS]: {
    titleKey: 'page.admin.users.title',
    descriptionKey: 'page.admin.users.meta-description',
  },
  [PAGE_NAMES.ADMIN_CONSULTATIONS]: {
    titleKey: 'page.admin.consultations.title',
    descriptionKey: 'page.admin.consultations.meta-description',
  },
  [PAGE_NAMES.ADMIN]: {
    titleKey: 'page.admin.title',
    descriptionKey: 'page.admin.meta-description',
  },
  [PAGE_NAMES.ADMIN_POSTS]: {
    titleKey: 'page.admin.posts.title',
    descriptionKey: 'page.admin.posts.meta-description',
  },
};

const getLocalizedHost = (lng: string): string => `${BASE_URL}${lng === 'ua' ? '' : `/${lng}`}`;

const toAliasString = (alias?: string | string[]): string | undefined => {
  if (typeof alias === 'string') {
    return alias;
  }

  return Array.isArray(alias) ? alias[0] : undefined;
};

const fallback = (value?: string, defaultValue = ''): string => value ?? defaultValue;

const buildOpenGraph = ({
  t,
  title,
  description,
  url,
  images,
  type = 'website',
}: {
  t: TranslateFunction;
  title: string;
  description: string;
  url: string;
  images?: OpenGraphImage[];
  type?: OpenGraphType;
}): OpenGraphPropsType => ({
  title,
  description,
  url,
  siteName: t('text.prana-club'),
  images: images?.length ? images : [{ url: DEFAULT_OG_IMAGE }],
  type,
});

const createStaticPageConfig = ({
  t,
  path,
  host,
  definition,
}: {
  t: TranslateFunction;
  path: string;
  host: string;
  definition: StaticPageDefinition;
}): PageConfigType => {
  const title = t(definition.titleKey);
  const metaDescription = t(definition.descriptionKey);
  const url = `${host}${definition.url ?? path}`;

  return {
    path,
    title,
    metaTitle: title,
    metaDescription,
    openGraph: buildOpenGraph({
      t,
      title,
      description: metaDescription,
      url,
      type: definition.ogType,
    }),
  };
};

export const getPageConfig = ({
  t,
  path,
  lng,
  title,
  image,
  filter,
  alias,
}: GetPageConfigPropsType): PageConfigType => {
  const host = getLocalizedHost(lng);
  const aliasString = toAliasString(alias);
  const staticDefinition = STATIC_PAGE_DEFINITIONS[path];

  if (staticDefinition) {
    return createStaticPageConfig({
      t,
      path,
      host,
      definition: staticDefinition,
    });
  }

  switch (path) {
    case PAGE_NAMES.COURSE: {
      const pageTitle = t('page.course.title');
      const metaDescription = t('page.course.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/courses/${fallback(aliasString)}`,
        }),
      };
    }

    case PAGE_NAMES.POST: {
      const metaTitle = fallback(title);
      const metaDescription = t('page.post.meta-description');

      return {
        path,
        title: t('page.post.title'),
        metaTitle,
        metaDescription,
        alias: aliasString,
        openGraph: buildOpenGraph({
          t,
          title: metaTitle,
          description: metaDescription,
          url: `${host}/posts/${fallback(aliasString)}`,
          type: 'article',
          images: image ? [{ url: image }] : undefined,
        }),
      };
    }

    case PAGE_NAMES.POSTS_TAGS: {
      const pageTitle = t('page.posts-tags.title');
      const metaDescription = t('page.posts-tags.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: pageTitle,
        metaDescription,
        filter,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/posts/tags/${fallback(aliasString)}`,
        }),
      };
    }

    case PAGE_NAMES.CATEGORY: {
      const pageTitle = t('page.category.title');
      const metaDescription = t('page.category.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        filter,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/categories/${fallback(aliasString)}`,
        }),
      };
    }

    case PAGE_NAMES.PLATFORM_VIDEO_COURSE_METRICS: {
      const pageTitle = t('page.metrics.title');
      const metaDescription = t('page.metrics.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        alias: aliasString,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/platform/video-courses/${fallback(aliasString)}/metrics`,
        }),
      };
    }

    case PAGE_NAMES.PLATFORM_VIDEO_COURSE_DASHBOARD: {
      const pageTitle = t('page.user-course.title');
      const metaDescription = t('page.user-course.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        alias: aliasString,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/platform/video-courses/${fallback(aliasString)}/dashboard`,
        }),
      };
    }

    case PAGE_NAMES.VIDEO_COURSE: {
      const pageTitle = t('page.user-course.title');
      const metaDescription = t('page.user-course.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        alias: aliasString,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}/video-courses/${fallback(aliasString)}`,
        }),
      };
    }

    case PAGE_NAMES.PLATFORM_ONLINE_COURSE_BOARD: {
      const pageTitle = t('page.platform.online-course.title');
      const metaDescription = t('page.platform.online-course.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}${PAGE_NAMES.PLATFORM_ONLINE_COURSE_BOARD}`,
        }),
      };
    }

    case PAGE_NAMES.PLATFORM_VIDEO_COURSE_BOARD: {
      const pageTitle = t('page.platform.video-course.title');
      const metaDescription = t('page.platform.video-course.meta-description');

      return {
        path,
        title: pageTitle,
        metaTitle: `${pageTitle}: ${fallback(title)}`,
        metaDescription,
        openGraph: buildOpenGraph({
          t,
          title: pageTitle,
          description: metaDescription,
          url: `${host}${PAGE_NAMES.PLATFORM_VIDEO_COURSE_BOARD}`,
        }),
      };
    }

    default:
      return createStaticPageConfig({
        t,
        path: PAGE_NAMES.ADMIN_POSTS,
        host,
        definition: STATIC_PAGE_DEFINITIONS[PAGE_NAMES.ADMIN_POSTS]!,
      });
  }
};
